/**
 * People Learning / Clapper Arcade — contrato compartido entre el router,
 * el editor de contenido, el dashboard y el juego (via el bridge del host).
 *
 * - El CONTENIDO del juego (niveles, pasos, textos ×3 idiomas, links,
 *   respuestas) es un JSON editable por People sin deploys (spec §7). El
 *   schema zod de acá valida tanto en el server (saveContent) como en el
 *   editor del front (modo JSON crudo).
 * - El PROGRESO se indexa por stepId (spec §7.2: si el contenido cambia,
 *   los resultados viejos siguen válidos — cada resultado guarda un snapshot
 *   del título). Los puntos SIEMPRE se derivan server-side: |steps| × 100.
 */

import { addBusinessDaysISO, businessDaysBetweenISO } from './dates';
import { z } from 'zod';

export const COURSE_ID = 'clapper-arcade';
export const POINTS_PER_STEP = 100;

export const ARCADE_LANGS = ['es', 'en', 'pt'] as const;
export type ArcadeLang = (typeof ARCADE_LANGS)[number];

/** Texto por idioma; es es obligatorio, en/pt caen a es si faltan. */
const i18nTextSchema = z.object({
  es: z.string().max(4000),
  en: z.string().max(4000).optional(),
  pt: z.string().max(4000).optional(),
});
export type I18nText = z.infer<typeof i18nTextSchema>;

export const pickLang = (t: I18nText | undefined, lang: ArcadeLang): string =>
  t ? t[lang] || t.es : '';

/** URL por idioma (videos y links, correcciones Learning 07/09 §4): vacío ⇒ cae al general. */
const i18nUrlSchema = z.object({
  es: z.string().max(500).optional(),
  en: z.string().max(500).optional(),
  pt: z.string().max(500).optional(),
});
export type I18nUrl = z.infer<typeof i18nUrlSchema>;
export const pickUrl = (general: string | undefined, byLang: I18nUrl | undefined, lang: ArcadeLang): string | undefined =>
  (byLang && byLang[lang]) || general || undefined;

/** Imágenes subidas por Learning al AttachmentsBucket (portadas, avatar de Facu…). */
export const LEARNING_IMAGE_KEY_RE = /^learning\/[a-z0-9-]+\/[A-Za-z0-9._-]+\.(png|jpe?g|webp|gif)$/;

const checkItemSchema = z.object({
  id: z.string().min(1).max(60),
  kind: z.enum(['toggle', 'select', 'multi', 'neutral']),
  label: i18nTextSchema,
  sub: i18nTextSchema.optional(),
  /** select/multi: opciones con la(s) correcta(s) marcadas. */
  options: z
    .array(z.object({ id: z.string().min(1).max(60), label: i18nTextSchema, correct: z.boolean().optional() }))
    .max(12)
    .optional(),
  /** multi: true = debe marcarse, false = NO debe marcarse (intruso). */
  good: z.boolean().optional(),
});
export type ArcadeCheckItem = z.infer<typeof checkItemSchema>;

/** Los 6 minijuegos viven en código; su data interna es JSON editable. */
export const MINIGAME_KINDS = ['dial', 'cables', 'virtues', 'dragdrop', 'hotspot', 'impact_role'] as const;
export type MinigameKind = (typeof MINIGAME_KINDS)[number];

const stepBase = {
  id: z.string().min(1).max(60),
  title: i18nTextSchema,
  desc: i18nTextSchema.optional(),
  optional: z.boolean().optional(),
  /** Solo para país específico (ej. 'Colombia'); ausente = todos. */
  onlyCountry: z.string().max(40).optional(),
};

const stepSchema = z.discriminatedUnion('type', [
  z.object({
    ...stepBase,
    type: z.literal('video'),
    /** Link de Drive/YouTube; vacío ⇒ playbar simulada del prototipo. */
    videoUrl: z.string().max(500).optional(),
    /** Video por idioma (§4): si existe para el idioma elegido, pisa a videoUrl. */
    videoUrlByLang: i18nUrlSchema.optional(),
    externalLink: z
      .object({
        url: z.string().max(500),
        label: i18nTextSchema.optional(),
        /** Link por idioma (§4). */
        urlByLang: i18nUrlSchema.optional(),
      })
      .optional(),
    /** Duración de la playbar simulada (segundos) cuando no hay videoUrl. */
    durationSec: z.number().int().min(1).max(600).optional(),
    /** Nivel 5: si es true, el videoUrl sale de content.areaVideos[areaChosen]. */
    byArea: z.boolean().optional(),
  }),
  z.object({
    ...stepBase,
    type: z.literal('check'),
    intro: i18nTextSchema.optional(),
    items: z.array(checkItemSchema).min(1).max(12),
  }),
  z.object({
    ...stepBase,
    type: z.literal('minigame'),
    kind: z.enum(MINIGAME_KINDS),
    intro: i18nTextSchema.optional(),
    /** Data interna del minijuego (targets, pares, fragmentos, hotspots…). */
    data: z.record(z.unknown()),
    /** Ítems de checklist que acompañan al minijuego (ej. switch post-reto). */
    items: z.array(checkItemSchema).max(6).optional(),
  }),
]);
export type ArcadeStep = z.infer<typeof stepSchema>;

const levelSchema = z.object({
  id: z.string().min(1).max(40),
  /** Color de acento (hex de la paleta Winclap). */
  accent: z.string().max(20),
  title: i18nTextSchema,
  desc: i18nTextSchema.optional(),
  /**
   * Video de Facu que se muestra al entrar al nivel (user flow §10/§47/§59…):
   * el cuadro de Facu lo reproduce arriba del texto (uiStrings facu.lN).
   * Por idioma con fallback al general (correcciones §4).
   */
  facuVideoUrl: z.string().max(500).optional(),
  facuVideoUrlByLang: i18nUrlSchema.optional(),
  steps: z.array(stepSchema).min(1).max(30),
});
export type ArcadeLevel = z.infer<typeof levelSchema>;

export const arcadeContentSchema = z
  .object({
    /** Lo bumpea saveContent — el editor no lo toca. */
    version: z.number().int().min(0),
    /** Fecha de lanzamiento (ISO date) — corte new_clapper / clapper_antiguo. */
    launchDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    /** Opciones del select de área en la portada del juego. */
    areas: z.array(z.string().min(1).max(60)).min(1).max(30),
    /** Nivel 5 ramificado (spec §15): área → link del video del líder. */
    areaVideos: z.record(z.string(), z.string().max(500)),
    /** Carpeta de Drive donde Learning sube los videos (referencia). */
    driveFolder: z.string().max(500).optional(),
    levels: z.array(levelSchema).min(1).max(10),
    /**
     * Textos de la interfaz del juego (chrome: portada, HUD, mapa, botones…)
     * editables sin deploy (correcciones Learning 07/09 §1). Clave = la de
     * /clapper-arcade/ui-strings.json (anidadas con punto: "facu.l1"); lo que
     * no esté acá cae al default del juego.
     */
    uiStrings: z
      .object({
        es: z.record(z.string().max(2000)).optional(),
        en: z.record(z.string().max(2000)).optional(),
        pt: z.record(z.string().max(2000)).optional(),
      })
      .optional(),
    /** Imágenes del juego (§1 y §5): keys del bucket; el host las firma al iniciar. */
    assets: z
      .object({
        facuAvatarKey: z.string().regex(LEARNING_IMAGE_KEY_RE).optional(),
        coverImageKey: z.string().regex(LEARNING_IMAGE_KEY_RE).optional(),
        logoKey: z.string().regex(LEARNING_IMAGE_KEY_RE).optional(),
      })
      .optional(),
  })
  .superRefine((content, ctx) => {
    const seen = new Set<string>();
    for (const lvl of content.levels)
      for (const s of lvl.steps) {
        if (seen.has(s.id))
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: `stepId duplicado: ${s.id}` });
        seen.add(s.id);
      }
  });
export type ArcadeContent = z.infer<typeof arcadeContentSchema>;

// ─── Programas de la portada (cards editables por People) ────────────────────

export const learningProgramSchema = z.object({
  id: z.string().min(1).max(60),
  /** 'arcade' = el curso gamificado (no borrable); 'link' = recurso externo. */
  kind: z.enum(['arcade', 'link']),
  emoji: z.string().max(8).optional(),
  /** Imagen de portada de la card (§5); si está, reemplaza al emoji. */
  imageKey: z.string().regex(LEARNING_IMAGE_KEY_RE).optional(),
  title: i18nTextSchema,
  desc: i18nTextSchema.optional(),
  /** Solo kind 'link': se abre en pestaña nueva. */
  url: z.string().max(500).optional(),
});
export type LearningProgram = z.infer<typeof learningProgramSchema>;

export const learningProgramsSchema = z.object({
  programs: z.array(learningProgramSchema).min(1).max(30),
}).superRefine((v, ctx) => {
  if (!v.programs.some((p) => p.kind === 'arcade'))
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Debe existir el programa arcade' });
  const ids = new Set<string>();
  for (const p of v.programs) {
    if (ids.has(p.id)) ctx.addIssue({ code: z.ZodIssueCode.custom, message: `id duplicado: ${p.id}` });
    ids.add(p.id);
  }
});
export type LearningPrograms = z.infer<typeof learningProgramsSchema>;

export const DEFAULT_PROGRAMS: LearningPrograms = {
  programs: [
    {
      id: 'clapper-arcade',
      kind: 'arcade',
      emoji: '🕹️',
      title: { es: 'Onboarding: Clapper Arcade' },
      desc: {
        es: 'Tu onboarding nivel por nivel: el negocio, la cultura, AI Native, cuidado y tu equipo.',
        en: 'Your onboarding level by level: the business, the culture, AI Native, care and your team.',
        pt: 'Seu onboarding nível por nível: o negócio, a cultura, AI Native, cuidado e seu time.',
      },
    },
  ],
};

// ─── Progreso ────────────────────────────────────────────────────────────────

export const LEARNING_STATUSES = ['sin_iniciar', 'en_progreso', 'completado', 'no_elegible'] as const;
export type LearningStatus = (typeof LEARNING_STATUSES)[number];

export const LEARNING_CATEGORIES = ['new_clapper', 'clapper_antiguo'] as const;
export type LearningCategory = (typeof LEARNING_CATEGORIES)[number];

export const STATUS_LABELS_ES: Record<LearningStatus, string> = {
  sin_iniciar: 'Sin iniciar',
  en_progreso: 'En progreso',
  completado: 'Completado',
  no_elegible: 'No elegible',
};

export const CATEGORY_LABELS_ES: Record<LearningCategory, string> = {
  new_clapper: 'New Clapper',
  clapper_antiguo: 'Clapper Antiguo',
};

export interface LearningStepResult {
  completedAt: string;
  /** Snapshot del título al momento de completar (spec §7.2). */
  titleEs?: string;
  /** Detalle de las decisiones del check/minijuego (spec §6.3). */
  detail?: { answers?: Record<string, string | string[] | boolean>; note?: string };
}

export interface LearningProgress {
  email: string;
  courseId: string;
  /** SIEMPRE derivado server-side: |steps| × POINTS_PER_STEP. */
  points: number;
  steps: Record<string, LearningStepResult>;
  /** Ids de niveles completados (para resumir sin re-derivar). */
  levelsDone: string[];
  startedAt?: string;
  finishedAt?: string;
  /** Monótono (max-merge): dos tabs nunca lo achican. */
  elapsedMs: number;
  lang?: ArcadeLang;
  areaChosen?: string;
  contentVersionAtStart?: number;
  noElegible?: { setBy: string; setAt: string; reason?: string } | null;
  /** Pruebas de Learning (§8): todos los niveles desbloqueados para esta persona. */
  unlockAll?: { setBy: string; setAt: string } | null;
  /** Product Tour del Studio: snapshot de la interfaz del recorrido para retomar exacto (JSON). */
  uiState?: string;
  updatedAt: string;
}

/** Detalle de una persona para el dashboard (§7): pasos con sus respuestas. */
export interface LearningUserDetailStep {
  stepId: string;
  levelId: string;
  levelTitleEs: string;
  titleEs: string;
  type: 'video' | 'check' | 'minigame';
  optional: boolean;
  completedAt: string | null;
  detail?: LearningStepResult['detail'];
}
export interface LearningUserDetail {
  email: string;
  progress: LearningProgress | null;
  steps: LearningUserDetailStep[];
}

/** Evento que el juego reporta al host (batch en reportProgress). */
export interface LearningEvent {
  /** `ui_state`: snapshot de la interfaz del Product Tour (retomar exacto); no suma pasos ni puntos. */
  kind: 'session_started' | 'step_completed' | 'level_completed' | 'finished' | 'ui_state';
  stepId?: string;
  levelId?: string;
  titleEs?: string;
  detail?: { answers?: Record<string, string | string[] | boolean>; note?: string };
  lang?: ArcadeLang;
  areaChosen?: string;
  uiState?: string;
  elapsedMs: number;
  at: string;
}

export const learningEventSchema: z.ZodType<LearningEvent> = z.object({
  kind: z.enum(['session_started', 'step_completed', 'level_completed', 'finished', 'ui_state']),
  stepId: z.string().max(60).optional(),
  levelId: z.string().max(40).optional(),
  titleEs: z.string().max(300).optional(),
  detail: z
    .object({
      answers: z.record(z.union([z.string().max(2000), z.array(z.string().max(200)).max(20), z.boolean()])).optional(),
      note: z.string().max(2000).optional(),
    })
    .optional(),
  lang: z.enum(ARCADE_LANGS).optional(),
  areaChosen: z.string().max(60).optional(),
  uiState: z.string().max(60_000).optional(),
  elapsedMs: z.number().int().min(0).max(1000 * 60 * 60 * 24 * 30),
  at: z.string().max(40),
});

export function deriveStatus(p: Pick<LearningProgress, 'startedAt' | 'finishedAt' | 'noElegible'> | null): LearningStatus {
  if (p?.noElegible) return 'no_elegible';
  if (p?.finishedAt) return 'completado';
  if (p?.startedAt) return 'en_progreso';
  return 'sin_iniciar';
}

/**
 * New Clapper ⟺ ingresó el día de lanzamiento o después (doc de Learning,
 * ajustes 14/09: "ingresos del 21/09/2026 en adelante" son obligatorios; del
 * 20/09 para atrás, Clapper Antiguo, opcional). `launchDate` es la fecha de
 * corte del contenido (editable por Learning). Sin fecha de ingreso ⇒
 * new_clapper (fallo abierto hacia el onboarding).
 */
export function deriveCategory(startDateIso: string | null | undefined, launchDate: string): LearningCategory {
  if (!startDateIso) return 'new_clapper';
  const start = isoDay(startDateIso);
  const cut = isoDay(launchDate);
  if (!start || !cut) return 'new_clapper';
  return start < cut ? 'clapper_antiguo' : 'new_clapper';
}

// ─── Plazo (días hábiles) ────────────────────────────────────────────────────

/** Plazo estándar para New Clappers: 8 días hábiles desde el ingreso (doc 14/09). */
export const MANDATORY_BUSINESS_DAYS = 8;
/** Un video "visto" en menos de esto desde el paso anterior es sospechoso (doc: "¿realmente está viendo los videos?"). */
export const FAST_VIDEO_SECONDS = 45;

/**
 * 'YYYY-MM-DD' de una fecha como la trae el community map: ISO (con o sin
 * hora), 'd/m/aaaa' o 'd-m-aaaa'. null si no parsea.
 */
export function isoDay(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const v = raw.trim();
  let y: number; let mo: number; let d: number;
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(v);
  const dmy = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/.exec(v);
  if (iso) { y = Number(iso[1]); mo = Number(iso[2]); d = Number(iso[3]); }
  else if (dmy) { d = Number(dmy[1]); mo = Number(dmy[2]); y = Number(dmy[3]); }
  else return null;
  const date = new Date(Date.UTC(y, mo - 1, d));
  if (Number.isNaN(date.getTime()) || date.getUTCMonth() !== mo - 1 || date.getUTCDate() !== d) return null;
  return date.toISOString().slice(0, 10);
}

/** Zona horaria del negocio: el plazo se cuenta en días de Buenos Aires. */
export const BUSINESS_TZ = 'America/Argentina/Buenos_Aires';

/**
 * Día ('YYYY-MM-DD') de un timestamp visto desde Buenos Aires. Un cierre a las
 * 22:30 AR del último día es "2026-10-02T01:30Z" en UTC: sin esto contaba tarde.
 * Las fechas sin hora se toman como están.
 */
export function isoDayInTz(raw: string | null | undefined, tz: string = BUSINESS_TZ): string | null {
  if (!raw) return null;
  const v = raw.trim();
  if (!/^\d{4}-\d{2}-\d{2}T/.test(v)) return isoDay(v);
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
}

/** Suma n días hábiles (lun-vie, sin feriados) a un 'YYYY-MM-DD'. Vive en shared/dates. */
export const addBusinessDays = addBusinessDaysISO;

/** Días hábiles entre dos fechas. Vive en shared/dates. */
export const businessDaysBetween = businessDaysBetweenISO;

/** Fecha límite (ISO date) para completar el curso; null sin fecha de ingreso. */
export function deadlineFor(startDateIso: string | null | undefined): string | null {
  const start = isoDay(startDateIso);
  return start ? addBusinessDays(start, MANDATORY_BUSINESS_DAYS) : null;
}

export type PlazoKind = 'on_time' | 'late' | 'due' | 'overdue' | 'na';
export interface Plazo {
  kind: PlazoKind;
  /** Días hábiles que quedan (due) o que pasaron (overdue). */
  days: number;
  deadline: string | null;
}

/**
 * Estado del plazo de una persona al día `today` (ISO date):
 *  - completado: on_time / late según finishedAt vs deadline;
 *  - pendiente: due (quedan N días hábiles) u overdue (pasaron N);
 *  - na: sin plazo (Clapper Antiguo, no elegible o sin fecha de ingreso).
 */
export function plazoFor(
  row: Pick<LearningDashboardRow, 'category' | 'status' | 'startDate' | 'finishedAt'> & { testMode?: boolean },
  today: string
): Plazo {
  const deadline = deadlineFor(row.startDate);
  if (!deadline || row.category !== 'new_clapper' || row.status === 'no_elegible' || row.testMode) return { kind: 'na', days: 0, deadline };
  if (row.status === 'completado') {
    const fin = isoDayInTz(row.finishedAt) ?? today;
    return { kind: fin <= deadline ? 'on_time' : 'late', days: Math.abs(businessDaysBetween(deadline, fin)), deadline };
  }
  const left = businessDaysBetween(today, deadline);
  return left >= 0 ? { kind: 'due', days: left, deadline } : { kind: 'overdue', days: -left, deadline };
}

// ─── Señal de visualización real (engagement) ────────────────────────────────

export interface LearningEngagement {
  videosDone: number;
  /** Videos marcados como vistos a menos de FAST_VIDEO_SECONDS del paso anterior. */
  fastVideos: number;
  lastActivityAt: string | null;
  level: 'none' | 'ok' | 'suspicious';
}

/**
 * Sin telemetría de reproducción, la señal es el tiempo entre pasos: un video
 * completado 20 segundos después del anterior no se vio. `videos` = pasos tipo
 * video del contenido vigente → segundos mínimos plausibles (45 s, o la mitad
 * de la duración declarada si es un video corto).
 */
export function videoThresholds(content: Pick<ArcadeContent, 'levels'>): Map<string, number> {
  const out = new Map<string, number>();
  for (const l of content.levels)
    for (const st of l.steps)
      if (st.type === 'video') out.set(st.id, st.durationSec ? Math.min(FAST_VIDEO_SECONDS, Math.floor(st.durationSec / 2)) : FAST_VIDEO_SECONDS);
  return out;
}

export function deriveEngagement(
  progress: Pick<LearningProgress, 'steps' | 'startedAt' | 'updatedAt'> | null,
  videos: Map<string, number> | Set<string>
): LearningEngagement {
  const thresholdOf = (id: string): number | null =>
    videos instanceof Map ? (videos.has(id) ? videos.get(id)! : null) : videos.has(id) ? FAST_VIDEO_SECONDS : null;
  if (!progress || Object.keys(progress.steps).length === 0) {
    return { videosDone: 0, fastVideos: 0, lastActivityAt: progress?.startedAt ?? null, level: 'none' };
  }
  const events = Object.entries(progress.steps)
    .map(([id, r]) => ({ id, at: Date.parse(r.completedAt) }))
    .filter((e) => !Number.isNaN(e.at))
    .sort((a, b) => a.at - b.at);
  let prev = progress.startedAt ? Date.parse(progress.startedAt) : NaN;
  let videosDone = 0;
  let fastVideos = 0;
  for (const e of events) {
    const min = thresholdOf(e.id);
    if (min !== null) {
      videosDone += 1;
      if (!Number.isNaN(prev) && e.at - prev < min * 1000) fastVideos += 1;
    }
    prev = e.at;
  }
  const last = events.length ? new Date(events[events.length - 1].at).toISOString() : progress.updatedAt ?? null;
  return { videosDone, fastVideos, lastActivityAt: last, level: fastVideos > 0 ? 'suspicious' : 'ok' };
}

// ─── Dashboard ───────────────────────────────────────────────────────────────

export interface LearningDashboardRow {
  email: string;
  name: string;
  role: string;
  team: string;
  area: string;
  /** Texto crudo del community map (email o "Nombre Apellido"). */
  peopleManager: string;
  /** Líder resuelto contra el community map (para "Resumen por líder" y el filtro Líder). */
  leaderEmail: string | null;
  leaderName: string;
  country: string;
  startDate: string | null;
  /** Fecha límite del curso (ingreso + 8 días hábiles); null sin ingreso. */
  deadline: string | null;
  engagement: LearningEngagement;
  /** Modo prueba (§8, todos los niveles desbloqueados): no impacta ninguna métrica. */
  testMode: boolean;
  status: LearningStatus;
  category: LearningCategory;
  points: number;
  /** 0..1 sobre los pasos NO opcionales del contenido vigente. */
  progressPct: number;
  stepsDone: number;
  checksDone: number;
  startedAt: string | null;
  finishedAt: string | null;
  elapsedMs: number;
  lang: string | null;
  areaChosen: string | null;
}

// ─── Métricas del dashboard (doc de Learning, ajustes 14/09 §2) ──────────────

export interface Ratio {
  pct: number;
  done: number;
  total: number;
}
const ratio = (done: number, total: number): Ratio => ({ pct: total ? Math.round((done / total) * 100) : 0, done, total });

/** Elegibles: no marcados "No elegible" ni en modo prueba (esos no impactan la data). */
export const eligible = (rows: LearningDashboardRow[]) => rows.filter((r) => r.status !== 'no_elegible' && !r.testMode);
export const newClappers = (rows: LearningDashboardRow[]) => eligible(rows).filter((r) => r.category === 'new_clapper');
export const oldClappers = (rows: LearningDashboardRow[]) => eligible(rows).filter((r) => r.category === 'clapper_antiguo');

/**
 * Cumplimiento New Clappers: % de los ingresos desde la fecha de corte que
 * completaron el curso al 100% DENTRO del plazo (8 días hábiles). Denominador:
 * todos los New Clappers elegibles (los que todavía están en plazo cuentan
 * como no cumplidos — `pending` los informa aparte).
 */
export function cumplimientoNewClappers(rows: LearningDashboardRow[], today: string): Ratio & { pending: number; overdue: number; late: number } {
  const news = newClappers(rows);
  let done = 0;
  let pending = 0;
  let overdue = 0;
  let late = 0;
  for (const r of news) {
    const p = plazoFor(r, today);
    if (p.kind === 'on_time') done += 1;
    else if (p.kind === 'late') late += 1;
    else if (p.kind === 'due') pending += 1;
    else if (p.kind === 'overdue') overdue += 1;
  }
  return { ...ratio(done, news.length), pending, overdue, late };
}

/** Adopción voluntaria: % de Clappers Antiguos que completaron el curso al 100%. */
export function adopcionVoluntaria(rows: LearningDashboardRow[]): Ratio {
  const olds = oldClappers(rows);
  return ratio(olds.filter((r) => r.status === 'completado').length, olds.length);
}

/** Avance promedio (0-100) de los Clappers Antiguos que iniciaron el curso. */
export function avancePromedioIniciados(rows: LearningDashboardRow[]): { pct: number; n: number } {
  const started = oldClappers(rows).filter((r) => r.status === 'en_progreso' || r.status === 'completado');
  const pct = started.length ? Math.round((started.reduce((a, r) => a + r.progressPct, 0) / started.length) * 100) : 0;
  return { pct, n: started.length };
}

/** Conteo por estado sobre un subconjunto (cards de Mi equipo / New Clappers). */
export function countByStatus(rows: LearningDashboardRow[]) {
  const el = eligible(rows);
  return {
    total: rows.length,
    elegibles: el.length,
    completados: el.filter((r) => r.status === 'completado').length,
    enProgreso: el.filter((r) => r.status === 'en_progreso').length,
    sinIniciar: el.filter((r) => r.status === 'sin_iniciar').length,
    noElegibles: rows.length - el.length,
  };
}

export interface GroupMetrics {
  key: string;
  label: string;
  total: number;
  newClappers: number;
  oldClappers: number;
  cumplimiento: Ratio & { pending: number; overdue: number };
  adopcion: Ratio;
}

/** Cumplimiento y adopción por área / país / líder (para gráficos y "Resumen por líder"). */
export function metricsBy(
  rows: LearningDashboardRow[],
  keyOf: (r: LearningDashboardRow) => { key: string; label: string },
  today: string
): GroupMetrics[] {
  const groups = new Map<string, { label: string; rows: LearningDashboardRow[] }>();
  for (const r of rows) {
    const { key, label } = keyOf(r);
    const g = groups.get(key) ?? { label, rows: [] };
    g.rows.push(r);
    groups.set(key, g);
  }
  return [...groups.entries()]
    .map(([key, g]) => ({
      key,
      label: g.label,
      total: g.rows.length,
      newClappers: newClappers(g.rows).length,
      oldClappers: oldClappers(g.rows).length,
      cumplimiento: cumplimientoNewClappers(g.rows, today),
      adopcion: adopcionVoluntaria(g.rows),
    }))
    .sort((a, b) => a.label.localeCompare(b.label, 'es'));
}
